import uniq from 'lodash/uniq';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { ApiArbitrage, ApiCexAccounts, ApiCexPairs, ApiPairs } from 'api';
import { useTypedDispatch, useTypedSelector } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { ICexAccountShort } from 'types/accounts';
import { IArbitrageConfig } from 'types/arbitrage';
import { ICexPair, IDexPair } from 'types/pairs';
import { IProject } from 'types/project';
import { PageNotFound } from 'ui/PageNotFound';
import { Spinner } from 'ui/Spinner/Spinner';

interface IArbitrageContext {
  project: IProject | null;
  isLoading: boolean;
  arbitrageConfig: IArbitrageConfig | undefined;
  getArbitrageConfig: ({
    shadow,
    update,
  }: {
    shadow: boolean;
    update: boolean;
  }) => Promise<IArbitrageConfig | undefined>;
  updateArbitrageConfig: (
    config: IArbitrageConfig,
    {
      showModal,
      successTitle,
      onSuccess,
    }: { showModal: boolean; successTitle?: string; onSuccess?: () => void },
  ) => Promise<void>;
  addNewPairs: (config: IArbitrageConfig) => Promise<void>;
  deleteArbitrageCexPair: (pairId: number) => Promise<void>;
  deleteArbitrageDexPair: (pairId: number) => Promise<void>;
  cexPairs: ICexPair[];
  dexPairs: IDexPair[];
  usedCexPairs: ICexPair[];
  usedDexPairs: IDexPair[];
  usedAccounts: ICexAccountShort[];
}

export const ArbitrageContext = createContext<IArbitrageContext>({
  project: null,
  isLoading: false,
  arbitrageConfig: undefined,
  getArbitrageConfig: async () => undefined,
  updateArbitrageConfig: async () => {},
  addNewPairs: async () => {},
  deleteArbitrageCexPair: async () => {},
  deleteArbitrageDexPair: async () => {},
  cexPairs: [],
  dexPairs: [],
  usedCexPairs: [],
  usedDexPairs: [],
  usedAccounts: [],
});

interface IArbitrageContextProviderProps {
  children?: React.ReactNode;
  projectId: number;
  configId: number;
}

const ArbitrageContextProvider = ({
  children,
  projectId,
  configId,
}: IArbitrageContextProviderProps) => {
  const dispatch = useTypedDispatch();
  const projects = useTypedSelector(store => store.projects.projects);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [arbitrageConfig, setArbitrageConfig] = useState<IArbitrageConfig | undefined>(undefined);
  const [cexPairs, setCexPairs] = useState<ICexPair[]>([]);
  const [dexPairs, setDexPairs] = useState<IDexPair[]>([]);
  const [usedCexPairs, setUsedCexPairs] = useState<ICexPair[]>([]);
  const [usedDexPairs, setUsedDexPairs] = useState<IDexPair[]>([]);
  const [usedAccounts, setUsedAccounts] = useState<ICexAccountShort[]>([]);

  const project = useMemo(
    () => (projects ?? []).find(project => project.id === projectId) ?? null,
    [projects, projectId],
  );

  const getCexPairs = useCallback(async () => {
    try {
      if (project) {
        if (!project.cexPairs) return;

        const _cexPairs = (
          await Promise.all(project.cexPairs.map(pair => ApiCexPairs.getCexPairById(pair.id)))
        )
          .filter(el => el.data)
          .map(el => ({ ...el.data }) as ICexPair);

        setCexPairs(_cexPairs);
        return _cexPairs;
      }
    } catch (error) {
      console.log(error);
    }
  }, [project]);

  const getDexPairs = useCallback(async () => {
    try {
      if (project) {
        if (!project.dexPairs) return;

        const _dexPairs = (
          await Promise.all(project.dexPairs.map(pair => ApiPairs.getDexPairById(pair.id)))
        )
          .filter(el => el.data)
          .map(el => ({ ...el.data }) as IDexPair);

        setDexPairs(_dexPairs);
        return _dexPairs;
      }
    } catch (error) {
      console.log(error);
    }
  }, [project]);

  const getUsedAccounts = useCallback(
    async (arbitrage_config: IArbitrageConfig | undefined) => {
      if (!arbitrage_config) return;

      try {
        const arbitrageAccounts = uniq((arbitrage_config.pairs_cex ?? []).map(el => el.account_id));
        const unloadedAccounts = arbitrageAccounts.filter(
          el => !usedAccounts.find(acc => acc.id === el),
        );

        if (unloadedAccounts.length === 0) return;

        const accounts = (
          await Promise.all(
            unloadedAccounts.map(
              async accId => (await ApiCexAccounts.getAccountById({ account_id: accId })).data,
            ),
          )
        ).filter(Boolean) as ICexAccountShort[];

        const newUsedAccounts = [...usedAccounts]
          .concat(accounts)
          .filter(el => !!arbitrageAccounts.find(account => account === el.id));

        setUsedAccounts(newUsedAccounts);
      } catch (error) {
        console.log(error);
      }
    },
    [usedAccounts],
  );

  const getArbitrageConfig = useCallback(
    async ({ shadow, update }: { shadow: boolean; update: boolean }) => {
      if (!shadow) {
        setIsLoading(true);
      }

      try {
        const {
          data: configs,
          isSuccess,
          errorMessage,
        } = await ApiArbitrage.getArbitrageConfigs({ projectId });

        if (isSuccess) {
          const findConfig = (configs ?? []).find(el => el.id === configId);

          getUsedAccounts(findConfig);

          if (findConfig && update) {
            setArbitrageConfig(findConfig);
          }

          return findConfig;
        } else {
          dispatch(
            setAlertState({
              type: 'failed-img',
              text: errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
        }
      } catch (error) {
        console.log('error: ', error);

        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      } finally {
        if (!shadow) {
          setIsLoading(false);
        }
      }
    },
    [configId, dispatch, projectId, getUsedAccounts],
  );

  useEffect(() => {
    const setup = async () => {
      setIsInitialLoading(true);

      const [cexPairs, dexPairs] = await Promise.all([getCexPairs(), getDexPairs()]);
      const config = await getArbitrageConfig({ shadow: false, update: true });

      if (config) {
        setUsedCexPairs(
          (config.pairs_cex ?? [])
            .map(el => cexPairs?.find(p => p.id === el.pair_id) ?? null)
            .filter(el => el !== null),
        );
        setUsedDexPairs(
          (config.pairs_dex ?? [])
            .map(el => dexPairs?.find(p => p.id === el.pair_id) ?? null)
            .filter(el => el !== null),
        );
      }

      setIsInitialLoading(false);
    };

    setup();

    //eslint-disable-next-line
  }, [configId, projectId, getCexPairs]);

  useEffect(() => {
    const updateInterval = setInterval(async () => {
      const config = await getArbitrageConfig({ shadow: true, update: false });

      if (config) {
        setArbitrageConfig(v =>
          v === undefined ? v : { ...v, active: config.active, stop_reason: config.stop_reason },
        );
      }
    }, 10 * 1000);

    return () => {
      clearInterval(updateInterval);
    };
  }, [configId, projectId, getArbitrageConfig]);

  const updateArbitrageConfig = useCallback(
    async (
      config: IArbitrageConfig,
      {
        showModal,
        successTitle,
        onSuccess,
      }: { showModal: boolean; successTitle?: string; onSuccess?: () => void },
    ) => {
      try {
        setIsLoading(true);

        const { isSuccess, errorMessage } = await ApiArbitrage.updateArbitrageConfig(config);

        if (isSuccess) {
          setArbitrageConfig(config);

          if (onSuccess) {
            onSuccess();
          }

          if (showModal) {
            dispatch(
              setAlertState({
                type: 'success',
                text: successTitle ?? 'Successfully updated arbitrage config',
                onClose: () => dispatch(dropAlertState()),
                onSubmit: () => {
                  dispatch(dropAlertState());
                },
              }),
            );
          }
        } else {
          dispatch(
            setAlertState({
              type: 'failed-img',
              text: errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
        }
      } catch (error) {
        console.log('error: ', error);

        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  const deleteArbitrageCexPair = useCallback(
    async (pairId: number) => {
      if (!arbitrageConfig) return;

      updateArbitrageConfig(
        {
          ...arbitrageConfig,
          pairs_cex: (arbitrageConfig.pairs_cex ?? []).filter(el => el.pair_id !== pairId),
        },
        {
          showModal: true,
          successTitle: 'You have successfully deleted',
          onSuccess: () => setUsedCexPairs(v => v.filter(el => el.id !== pairId)),
        },
      );
    },
    [arbitrageConfig, updateArbitrageConfig],
  );

  const deleteArbitrageDexPair = useCallback(
    async (pairId: number) => {
      if (!arbitrageConfig) return;

      updateArbitrageConfig(
        {
          ...arbitrageConfig,
          pairs_dex: (arbitrageConfig.pairs_dex ?? []).filter(el => el.pair_id !== pairId),
        },
        {
          showModal: true,
          successTitle: 'You have successfully deleted',
          onSuccess: () => setUsedDexPairs(v => v.filter(el => el.id !== pairId)),
        },
      );
    },
    [arbitrageConfig, updateArbitrageConfig],
  );

  const addNewPairs = useCallback(
    async (newConfig: IArbitrageConfig) => {
      if (!arbitrageConfig) return;

      updateArbitrageConfig(newConfig, {
        showModal: true,
        successTitle: 'You have successfully added new pairs to arbitrage!',
        onSuccess: () => {
          const cexIds = (newConfig.pairs_cex ?? []).map(el => el.pair_id);
          const dexIds = (newConfig.pairs_dex ?? []).map(el => el.pair_id);

          const _cexPairs = cexIds
            .map(el => cexPairs.find(c => c.id === el) ?? null)
            .filter(el => el !== null);

          setUsedCexPairs(_cexPairs);

          const _dexPairs = dexIds
            .map(el => dexPairs.find(c => c.id === el) ?? null)
            .filter(el => el !== null);

          setUsedDexPairs(_dexPairs);
        },
      });
    },
    [cexPairs, dexPairs, arbitrageConfig, updateArbitrageConfig],
  );

  return (
    <ArbitrageContext.Provider
      value={{
        project,
        isLoading,
        arbitrageConfig,
        getArbitrageConfig,
        updateArbitrageConfig,
        deleteArbitrageCexPair,
        deleteArbitrageDexPair,
        addNewPairs,
        cexPairs,
        dexPairs,
        usedCexPairs,
        usedDexPairs,
        usedAccounts,
      }}
    >
      {!isInitialLoading && !arbitrageConfig ? (
        <PageNotFound />
      ) : (
        <>
          {isInitialLoading ? (
            <div className="w-full flex items-center justify-center p-6">
              <Spinner size="medium" />
            </div>
          ) : (
            children
          )}
        </>
      )}
    </ArbitrageContext.Provider>
  );
};

export default ArbitrageContextProvider;
